import React, { useEffect } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';

import styles from './Credit.module.scss';
import Button from '../../../../components/button/button';
import FormCheckbox from '../../../../components/formCheckbox/FormCheckbox';
import FormInput from '../../../../components/formInput/FormInput';
import Portal from '../../../../components/portal/Portal';
import StepperNavigation from '../../../../components/stepper/StepperNavigation';
import InputContainer from '../../../../components/template/InputContainer/InputContainer';
import Notes from '../../../../components/template/Notes/Notes';
import SectionContainer from '../../../../components/template/SectionContainer/SectionContainer';
import Subtitle from '../../../../components/template/Subtitle/Subtitle';
import Title from '../../../../components/template/Title/Title';
import { QuestionForm } from '../../../../models/questionModel';
import useFormStore, { Form } from '../../../../store/FormStore';
import useQuestionStore from '../../../../store/QuestionStore';
import useStepperStore from '../../../../store/StepperStore';
import MainQuestion from '../MainQuestion/MainQuestion';

interface CreditProps {}

const Credit: React.FC<CreditProps> = ({}) => {
  const { setQuestion } = useQuestionStore();
  const { setStepper } = useStepperStore();
  const { setForm } = useFormStore();

  //#region Form
  const formMethods = useFormContext<Form>();
  const {
    getValues,
    reset,
    watch,
    setValue,
    formState: { isValid },
  } = formMethods;
  const { credit } = watch();
  //#endregion

  useEffect(() => {
    reset(getValues());
  }, []);

  //#region Handler
  const getCreditImpactQuestionNotes = (
    impactTitle:
      | 'Inventory Days'
      | 'Receivables Days'
      | 'Payables Days'
      | 'Working Capital Cycle'
  ) => {
    // --- Inventory Days ---
    const inventoryDays =
      ((Number(getValues('credit.inventoryDays.openingInventory')) +
        Number(getValues('credit.inventoryDays.closingInventory'))) /
        2 /
        Number(getValues('credit.inventoryDays.costOfGoodsSold'))) *
      365;

    // --- Receivables Days ---
    const receivablesDays =
      ((Number(getValues('credit.receivablesDays.openingReceivables')) +
        Number(getValues('credit.receivablesDays.closingReceivables'))) /
        2 /
        Number(getValues('credit.receivablesDays.revenue'))) *
      365;

    // --- Payables Days ---
    const payablesDays =
      ((Number(getValues('credit.payablesDays.openingPayables')) +
        Number(getValues('credit.payablesDays.closingPayables'))) /
        2 /
        Number(getValues('credit.payablesDays.costOfGoodsSold'))) *
      365;

    // --- Working Capital Cycle ---
    const wcc = inventoryDays + receivablesDays - payablesDays;

    // Get notes for the selected impact question
    const notes =
      CreditData.impactQuestions.find((imp) => imp.title === impactTitle)
        ?.notes || [];

    let value = 0;

    if (impactTitle === 'Inventory Days') {
      value = inventoryDays;
    } else if (impactTitle === 'Receivables Days') {
      value = receivablesDays;
    } else if (impactTitle === 'Payables Days') {
      value = payablesDays;
    } else {
      value = wcc;
    }

    const selectedNotes = notes.find((n) => value > n.min && value <= n.max);

    return {
      answer: `${value.toFixed(0)} days`,
      answerNotes: [selectedNotes?.label || ''],
      marks: selectedNotes?.marks || 0,
      question: impactTitle,
    };
  };

  const onHandleNavigation = (nav: 'next' | 'prev') => {
    if (!isValid && nav == 'next') return;

    const compulsoryQuestions = CreditData.compulsoryQuestions.map((comp) => {
      return {
        answer: getValues('credit.compulsory'),
        question: comp.question,
        questionNotes: [],
        answerNotes: [],
      };
    });

    const impactQuestions = CreditData.impactQuestions.map((imp) => {
      return {
        ...getCreditImpactQuestionNotes(imp.title as any),
        questionNotes: [],
      };
    });

    const probabilityQuestions = CreditData.probabilityQuestions.map((comp) => {
      return {
        answer:
          comp.inputs.find(
            (inp) => inp.value == Number(getValues('credit.probability'))
          )?.label || '',
        question: comp.question,
        questionNotes: [],
        answerNotes: [],
        marks: Number(getValues('credit.probability')),
      };
    });
    const finalCreditData = new QuestionForm(
      CreditData.title,
      CreditData.description,
      compulsoryQuestions,
      impactQuestions,
      probabilityQuestions
    );

    setQuestion({ credit: finalCreditData });

    setForm({ ...getValues() });

    setStepper(
      nav,
      undefined,
      credit?.compulsory == '' ? undefined : credit?.compulsory == 'Yes'
    );
  };

  //#endregion

  const CreditData = {
    title: 'Credit Risk',
    description:
      'Credit risk, or default risk, is the risk that a financial loss will be incurred if a counterpart to a (derivatives) transaction does not fulfil its financial obligations in a timely manner.',
    compulsoryQuestions: [
      {
        id: 'credit.compulsory',
        question: 'Is your Company exposed to credit risk?',
        inputs: [
          { value: 'Yes', label: 'Yes' },
          { value: 'Not applicable', label: 'Not applicable' },
        ],
      },
    ],
    probabilityQuestions: [
      {
        id: 'credit.probability',
        question: 'How frequent is the Company facing the risk?',
        inputs: [
          { value: 1, label: 'Improbable' },
          { value: 2, label: 'Remote' },
          { value: 3, label: 'Fairly Likely' },
          { value: 4, label: 'Probable' },
          { value: 5, label: 'Very Likely' },
        ],
      },
    ],
    impactQuestions: [
      {
        title: 'Inventory Days',
        objective:
          'Objective: Measures the average amount of time in which a company’s inventory is held on hand until it is sold.',
        notes: [
          {
            min: -Infinity,
            max: 90,
            marks: 5,
            label:
              'Low Inventory Days (<90 days) : High Near-Term Liquidity and More Cash on Hand',
          },
          {
            min: 90,
            max: Infinity,
            marks: 1,
            label:
              'High Inventory Days (>=90 days) : Less Near-Term Liquidity and Reduced Cash on Hand',
          },
        ],
        inputs: [
          {
            id: 'credit.inventoryDays.openingInventory',
            label: 'Opening Inventory',
          },
          {
            id: 'credit.inventoryDays.closingInventory',
            label: 'Closing Inventory',
          },
          {
            id: 'credit.inventoryDays.costOfGoodsSold',
            label: 'Cost of Goods Sold',
          },
        ],
      },
      {
        title: 'Receivables Days',
        objective:
          'Objective: Measures the average number of days it takes a company to collect payment after a sale on credit.',
        notes: [
          {
            min: -Infinity,
            max: 60,
            marks: 5,
            label:
              'Low A/R Days (<60 days) : Quicker Conversion from Credit Sales to Cash Collection',
          },
          {
            min: 60,
            max: Infinity,
            marks: 1,
            label:
              'High A/R Days (>=60 days): Prolonged Delay in Converting Credit Sales into Cash',
          },
        ],
        inputs: [
          {
            id: 'credit.receivablesDays.openingReceivables',
            label: 'Opening Receivables',
          },
          {
            id: 'credit.receivablesDays.closingReceivables',
            label: 'Closing Receivables',
          },
          { id: 'credit.receivablesDays.revenue', label: 'Revenue' },
        ],
      },
      {
        title: 'Payables Days',
        objective:
          'Objective: Measures number of days it takes for a company to fulfill an invoice from suppliers or vendors for orders placed using credit.',
        notes: [
          {
            min: -Infinity,
            max: 60,
            marks: 5,
            label:
              'Low A/P Days (<60 days) : Reduced Near-Term Liquidity and Less Free Cash Flow (FCF)',
          },
          {
            min: 60,
            max: Infinity,
            marks: 1,
            label:
              'High A/P Days (>=60 days): More Near-Term Liquidity and Increased Free Cash Flow (FCF)',
          },
        ],
        inputs: [
          {
            id: 'credit.payablesDays.openingPayables',
            label: 'Opening Payables',
          },
          {
            id: 'credit.payablesDays.closingPayables',
            label: 'Closing Payables',
          },
          {
            id: 'credit.payablesDays.costOfGoodsSold',
            label: 'Cost of Goods Sold',
          },
        ],
      },
      {
        title: 'Working Capital Cycle',
        objective:
          'Objective: Measure the length of time taken to convert the total net working capital (current assets less current liabilities) into cash.',
        notes: [
          {
            min: 0,
            max: Infinity,
            marks: 5,
            label:
              'Positive WCC: The company needs to pay off bills earlier as required some time to collect money back. However it is normal to have a business with positive working capital cycle.',
          },
          {
            min: -Infinity,
            max: 0,
            marks: 1,
            label:
              'Negative WCC: It could be efficiency in businesses with low inventory and accounts receivable. However the company may facing financial trouble if it doesn’t have enough cash to pay its current liabilities.',
          },
        ],
        inputs: [
          {
            id: 'credit.workingCapitalCycle.inventoryDays',
            label: 'Inventory Days',
          },
          {
            id: 'credit.workingCapitalCycle.receivablesDays',
            label: 'Receivables Days',
          },
          {
            id: 'credit.workingCapitalCycle.payableDays',
            label: 'Payable Days',
          },
        ],
      },
    ],
  };

  const generateRandomData = () => {
    const randomNumber = (min = 1, max = 1000) =>
      Math.floor(Math.random() * (max - min + 1)) + min;

    return {
      compulsory: Math.random() > 0.5 ? 'Yes' : 'Not applicable',
      probability: randomNumber(1, 5), // whole number

      debtToCapitalRatio: {
        totalDebt: randomNumber(1, 10000).toString(),
        shareholdersEquity: randomNumber(1, 10000).toString(),
      },

      debtsToAssetRatio: {
        totalDebt: randomNumber(1, 10000).toString(),
        totalAssets: randomNumber(1, 10000).toString(),
      },

      EBITDAMargin: {
        netProfit: randomNumber(1, 10000).toString(),
        interest: randomNumber(1, 10000).toString(),
        taxes: randomNumber(1, 10000).toString(),
        depreciationAndAmortisation: randomNumber(1, 10000).toString(),
        revenue: randomNumber(1, 10000).toString(),
      },

      inventoryDays: {
        openingInventory: randomNumber(1, 10000).toString(),
        closingInventory: randomNumber(1, 10000).toString(),
        costOfGoodsSold: randomNumber(1, 10000).toString(),
      },

      receivablesDays: {
        openingReceivables: randomNumber(1, 10000).toString(),
        closingReceivables: randomNumber(1, 10000).toString(),
        revenue: randomNumber(1, 10000).toString(),
      },

      payablesDays: {
        openingPayables: randomNumber(1, 10000).toString(),
        closingPayables: randomNumber(1, 10000).toString(),
        costOfGoodsSold: randomNumber(1, 10000).toString(),
      },

      workingCapitalCycle: {
        inventoryDays: randomNumber(1, 10000).toString(),
        receivablesDays: randomNumber(1, 10000).toString(),
        payableDays: randomNumber(1, 10000).toString(),
      },
    };
  };

  return (
    <FormProvider {...formMethods}>
      <div className={styles.container}>
        <MainQuestion
          id="credit"
          title={CreditData.title}
          description={CreditData.description}
          compulsoryQuestions={CreditData.compulsoryQuestions}
          probabilityQuestions={CreditData.probabilityQuestions}
        />

        {credit?.compulsory && credit?.compulsory == 'Yes' && (
          <SectionContainer>
            {CreditData.impactQuestions.map((impact, idx) => (
              <React.Fragment key={idx}>
                <Title>{impact.title}</Title>
                <Subtitle>{impact.objective}</Subtitle>

                <Notes key={idx}>
                  {impact.notes.map((n, idx) => (
                    <React.Fragment key={idx}>
                      {n.label} <br />
                    </React.Fragment>
                  ))}
                </Notes>

                <InputContainer>
                  {impact.inputs.map((inp, idx) => (
                    <FormInput
                      key={idx}
                      label={inp.label}
                      placeholder="Enter amount"
                      type="number"
                      id={inp.id}
                      required
                    />
                  ))}
                </InputContainer>
              </React.Fragment>
            ))}
          </SectionContainer>
        )}
      </div>

      <StepperNavigation
        isValid={isValid}
        onPrev={() => onHandleNavigation('prev')}
        onNext={() => onHandleNavigation('next')}
      />

      <Portal root="tester">
        <Button
          isCircle
          onClick={() => {
            reset({
              ...getValues(),
              credit: {
                ...getValues().credit,
                ...generateRandomData(),
              },
            });
          }}
        >
          Random
        </Button>
      </Portal>
    </FormProvider>
  );
};

export default Credit;
